import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { templatesAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Plus, FileText, Edit, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

export const Templates = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await templatesAPI.list();
            setTemplates(res.data.templates || res.data || []);
        } catch (error) {
            toast.error('Erro ao carregar templates');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Deseja remover este template?')) return;
        try {
            await templatesAPI.delete(id);
            toast.success('Template removido');
            loadData();
        } catch (error) {
            toast.error('Erro ao remover template');
        }
    };

    const handleDuplicate = async (template, e) => {
        e.stopPropagation();
        try {
            await templatesAPI.create({
                name: `${template.name} (cópia)`,
                subject: template.subject,
                blocks: template.blocks,
            });
            toast.success('Template duplicado');
            loadData();
        } catch (error) {
            toast.error('Erro ao duplicar template');
        }
    };

    return (
        <div data-testid="templates-page">
            <Header 
                title="Templates de Email" 
                subtitle="Crie modelos visuais para seus emails"
                actions={
                    <Button 
                        onClick={() => navigate('/templates/new')}
                        className="bg-lime-500 hover:bg-lime-400 text-gray-900"
                        data-testid="new-template-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Template
                    </Button>
                }
            />
            
            <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        <div className="col-span-full flex items-center justify-center h-64">
                            <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="col-span-full text-center py-16">
                            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhum template criado</h3>
                            <p className="text-sm text-gray-500 mb-4">Crie seu primeiro modelo de email</p>
                            <Button 
                                onClick={() => navigate('/templates/new')}
                                className="bg-lime-500 hover:bg-lime-400 text-gray-900"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Criar Template
                            </Button>
                        </div>
                    ) : (
                        templates.map((template) => (
                            <Card 
                                key={template.id} 
                                className="hover:shadow-md transition-shadow cursor-pointer group"
                                onClick={() => navigate(`/templates/${template.id}`)}
                                data-testid={`template-${template.id}`}
                            >
                                {/* Preview */}
                                <div className="h-40 bg-gray-100 border-b overflow-hidden">
                                    {template.blocks?.length > 0 ? (
                                        <div className="p-4 scale-50 origin-top-left w-[200%]">
                                            {template.blocks.slice(0, 3).map((block, i) => (
                                                <div key={i} className="mb-2">
                                                    {block.type === 'header' && (
                                                        <div className="h-6 bg-gray-300 rounded w-3/4 mx-auto" />
                                                    )}
                                                    {block.type === 'text' && (
                                                        <div className="space-y-1">
                                                            <div className="h-3 bg-gray-200 rounded w-full" />
                                                            <div className="h-3 bg-gray-200 rounded w-5/6" />
                                                        </div>
                                                    )}
                                                    {block.type === 'button' && (
                                                        <div className="h-8 bg-lime-400 rounded w-32 mx-auto" />
                                                    )}
                                                    {block.type === 'image' && (
                                                        <div className="h-16 bg-gray-300 rounded w-full" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <FileText className="w-8 h-8 text-gray-300" />
                                        </div>
                                    )}
                                </div>
                                
                                <CardHeader className="pb-2">
                                    <CardTitle className="font-heading text-lg">{template.name}</CardTitle>
                                    <p className="text-sm text-gray-500 truncate">{template.subject || 'Sem assunto'}</p>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-500">{formatDate(template.created_at)}</span>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                            <Button size="icon" variant="ghost" onClick={(e) => handleDuplicate(template, e)}>
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => navigate(`/templates/${template.id}`)}>
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={(e) => handleDelete(template.id, e)} className="text-red-600">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Templates;
